import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BookkeepingOption } from "@/types/review";
import { Check } from "lucide-react";
import { useTranslations } from "use-intl";

interface BookkeepingOptionCardProps {
  option: BookkeepingOption;
  onSelect?: (optionId: string) => void;
  isSelected?: boolean;
  disabled?: boolean;
}

const BookkeepingOptionCard: React.FC<BookkeepingOptionCardProps> = ({
  option,
  onSelect,
  isSelected = false,
  disabled = false,
}) => {
  const t = useTranslations("consultantReview.option");
  return (
    <Card
      className={`relative transition-all ${
        isSelected
          ? "border-primary bg-primary/5"
          : option.recommended
          ? "border-blue-200 bg-blue-50/50"
          : ""
      }`}
    >
      {option.recommended && !isSelected && (
        <Badge
          className="absolute -top-2 -right-2 bg-blue-500"
          variant="default"
        >
          {t("recommended")}
        </Badge>
      )}
      {isSelected && (
        <Badge
          className="absolute -top-2 -right-2 bg-green-500"
          variant="default"
        >
          <Check className="h-3 w-3 mr-1" />
          {disabled ? t("approved") : t("selected")}
        </Badge>
      )}

      <CardHeader>
        <CardTitle className="text-lg">{option.label}</CardTitle>
        <CardDescription>{option.description}</CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        <div>
          <h4 className="text-sm font-semibold mb-2">{t("whenToUse")}</h4>
          <p className="text-sm text-muted-foreground">{option.details}</p>
        </div>

        {option.accountingEntries && option.accountingEntries.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold mb-2">{t("entries")}</h4>
            <div className="space-y-1">
              {option.accountingEntries.map((entry, index) => (
                <div
                  key={index}
                  className="text-sm font-mono bg-muted px-3 py-1.5 rounded"
                >
                  {entry}
                </div>
              ))}
            </div>
          </div>
        )}

        {!disabled && onSelect && (
          <Button
            className="w-full"
            variant={isSelected ? "default" : "outline"}
            onClick={() => onSelect(option.id)}
          >
            {isSelected ? t("selected") : t("select")}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default BookkeepingOptionCard;
